import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Engineer } from '@/mocks/talentPool';

const SUBMIT_URL = 'https://readdy.ai/api/form/d88mah04og5fkb1kt3l0';

interface RequestMatchModalProps {
  engineer: Engineer | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function RequestMatchModal({ engineer, isOpen, onClose }: RequestMatchModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSubmitted(false);
      setErrors({});
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    const data = new FormData(form);
    const newErrors: Record<string, string> = {};

    if (!data.get('client_name')?.toString().trim()) newErrors.client_name = 'Name is required';
    if (!data.get('company')?.toString().trim()) newErrors.company = 'Company is required';
    const email = data.get('email')?.toString().trim() || '';
    if (!email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Enter a valid email';

    if (Object.keys(newErrors).length > 0) {
      e.preventDefault();
      setErrors(newErrors);
      return;
    }

    window.gtag('event', 'form_submit', {
      form_type: 'request_match',
      form_location: 'talent_pool',
      engineer_name: engineer.name,
      engineer_role: engineer.role,
    });

    setErrors({});
    setSubmitted(true);
  };

  if (!engineer) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-[60] flex items-start justify-center bg-navy-900/50 backdrop-blur-sm overflow-y-auto py-8 px-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-auto relative overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gray-50/80 border-b border-gray-100 p-5 md:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl ${engineer.avatarColor} flex items-center justify-center flex-shrink-0`}
                  >
                    <span className="text-white text-sm font-bold">{engineer.initials}</span>
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-navy-900">
                      Request Match with {engineer.name}
                    </h2>
                    <p className="text-sm text-gray-500">{engineer.role}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-navy-900 hover:border-gray-300 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              {/* Engineer Quick Stats */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="bg-white border border-gray-100 rounded-xl p-2.5 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">Experience</p>
                  <p className="text-sm font-bold text-navy-900">{engineer.years} years</p>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-2.5 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">Rate</p>
                  <p className="text-sm font-bold text-navy-900">${engineer.rateMin}–${engineer.rateMax}/hr</p>
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-2.5 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">Timezone</p>
                  <p className="text-sm font-bold text-navy-900">{engineer.timezoneOffset}</p>
                </div>
              </div>

              {/* Skills */}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {engineer.skills.map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs font-medium text-gray-600"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Form Body */}
            <div className="p-5 md:p-6">
              {!submitted ? (
                <form
                  id="request-match-form"
                  data-readdy-form
                  action={SUBMIT_URL}
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Hidden engineer fields */}
                  <div className="hidden">
                    <input type="text" name="engineer_name" value={engineer.name} readOnly />
                    <input type="text" name="engineer_role" value={engineer.role} readOnly />
                    <input type="text" name="engineer_skills" value={engineer.skills.join(', ')} readOnly />
                    <input
                      type="text"
                      name="engineer_rate"
                      value={`$${engineer.rateMin}–$${engineer.rateMax}/hr`}
                      readOnly
                    />
                    <input type="text" name="engineer_timezone" value={engineer.timezone} readOnly />
                    <input type="text" name="engineer_seniority" value={engineer.seniority} readOnly />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Name */}
                    <div>
                      <label htmlFor="client_name" className="block text-sm font-semibold text-navy-900 mb-1.5">
                        Your Name
                      </label>
                      <input
                        id="client_name"
                        type="text"
                        name="client_name"
                        placeholder="John Smith"
                        className={`w-full px-3.5 py-2.5 bg-gray-50 border rounded-lg text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all ${
                          errors.client_name ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-primary-400'
                        }`}
                      />
                      {errors.client_name && (
                        <p className="text-xs text-red-500 mt-1">{errors.client_name}</p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-navy-900 mb-1.5">
                        Company
                      </label>
                      <input
                        id="company"
                        type="text"
                        name="company"
                        placeholder="Acme Inc."
                        className={`w-full px-3.5 py-2.5 bg-gray-50 border rounded-lg text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all ${
                          errors.company ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-primary-400'
                        }`}
                      />
                      {errors.company && (
                        <p className="text-xs text-red-500 mt-1">{errors.company}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-navy-900 mb-1.5">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="john@acme.com"
                      className={`w-full px-3.5 py-2.5 bg-gray-50 border rounded-lg text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all ${
                        errors.email ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-primary-400'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Engagement Model */}
                    <div>
                      <label htmlFor="engagement_model" className="block text-sm font-semibold text-navy-900 mb-1.5">
                        Preferred Model
                      </label>
                      <select
                        id="engagement_model"
                        name="engagement_model"
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all appearance-none cursor-pointer"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      >
                        <option value="">Select a model</option>
                        <option value="staff-augmentation">Staff Augmentation</option>
                        <option value="dedicated-team">Dedicated Team</option>
                        <option value="project-based">Project-Based</option>
                        <option value="not-sure">Not Sure — Recommend One</option>
                      </select>
                    </div>

                    {/* Start Timeline */}
                    <div>
                      <label htmlFor="start_timeline" className="block text-sm font-semibold text-navy-900 mb-1.5">
                        When Do You Need to Start?
                      </label>
                      <select
                        id="start_timeline"
                        name="start_timeline"
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all appearance-none cursor-pointer"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                      >
                        <option value="">Select timeline</option>
                        <option value="immediately">Immediately</option>
                        <option value="1-2-weeks">Within 1–2 Weeks</option>
                        <option value="1-month">Within 1 Month</option>
                        <option value="flexible">Flexible / Exploring</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-navy-900 mb-1.5">
                      Project Details &amp; Requirements
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your project, tech stack, team size, and what you need this engineer to deliver..."
                      className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 transition-all resize-none"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 text-right">Max 500 characters</p>
                  </div>

                  {/* Submit */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-primary-500/20 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                    >
                      <span className="flex items-center justify-center gap-2">
                        Request Match with {engineer.name}
                        <i className="ri-send-plane-line group-hover:translate-x-0.5 transition-transform"></i>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-3 border-2 border-gray-200 text-navy-900 text-sm font-medium rounded-xl hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-8 md:py-12"
                >
                  <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-emerald-50 rounded-full">
                    <i className="ri-check-double-line text-2xl text-emerald-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-2">Request Sent!</h3>
                  <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto">
                    We have received your interest in matching with{' '}
                    <span className="font-semibold text-navy-900">{engineer.name}</span>. Our team will
                    reach out within 2 hours to schedule an intro call.
                  </p>
                  <button
                    onClick={onClose}
                    className="px-6 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all cursor-pointer"
                  >
                    Close
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}